import { gql } from '@apollo/client';
import { BlockSections } from '../../components/blocks/block-sections';
import { ILibraryLanguage } from './libraryLanguage';
import {
  ILibraryOpeningHours,
  LibraryOpeningHoursFragment,
} from './libraryOpeningHours';
import {
  ILibraryOpeningHoursException,
  LibraryOpeningHoursExceptionsFragment,
} from './libraryOpeningHoursException';
// import { TOccationTypes } from './meetingPlacePageOccations';
import { IMeetingPlacePageOfferList } from './meetingPlacePageOfferList';
import {
  ILibrarySocialMediaLinks,
  LibrarySocialMediaLinksFragment,
} from './librarySocialMediaLinks';
import { IMeetingPlaceLinkToAnyPage } from './meetingPlaceLinkToAnyPage';
import { SvgFragment } from './svg';
import { MainBannerBlockFragment } from './mainBannerBlock';
import { HomeTextAndImageCardBlockFragment } from './textAndImageCardBlock';
import { IMeetingPlacePageTenantShopList } from './meetingPlacePageTenantShopList';
import {
  ILibraryTenantAttributeFilters,
  LibraryTenantAttributeFiltersFragment,
} from './libraryTenantAttributeFilters';
import { IMeetingPlacePageServiceList } from './meetingPlacePageServiceList';

export interface ILibraryMeetingPlaceMinifiedCoordinates extends ILibraryMeetingPlaceMinified {
  coordinates: {
    lon: string;
    lat: string;
  };
}

export interface ILibraryMeetingPlaceAddress {
  visitorsAddressStreet: string;
  visitorsAddressCity: string;
  visitorsAddressZipCode: string;
  country: {
    name: string;
  };
}

export interface ILibraryMeetingPlaceMinifiedExtended
  extends ILibraryMeetingPlaceMinified, ILibraryMeetingPlaceMinifiedLogo {
  node_locale: string;
  mainBannerList: BlockSections;
  name: string;
  cardList: {
    cards: BlockSections[];
    showIkeaProductsCard: boolean;
  };
  socialMediaLinks: ILibrarySocialMediaLinks;
}

export interface ILibraryMeetingPlaceSeoRichMinifiedExtended
  extends
    ILibraryMeetingPlaceMinified,
    ILibraryMeetingPlaceMinifiedLogo,
    ILibraryMeetingPlaceMinifiedCoordinates,
    ILibraryMeetingPlaceMinifiedExtended,
    ILibraryMeetingPlaceAddress {
  phoneNumber: string;
  eMailAddress: string;
  webUrl: string;
  openingHours: ILibraryOpeningHours;
}

export interface ILibraryMeetingPlaceMinifiedOpeningHours extends ILibraryMeetingPlaceMinified {
  openingHours: ILibraryOpeningHours;
  visioGlobeMapKey?: string;
  visioGlobeFloorPlanPage?: {
    id: string;
    contentful_id: string;
    name: string;
    slug: string;
    node_locale: string;
  };
  openingHoursExceptions: ILibraryOpeningHoursException[];
  timezone: string;
  socialMediaLinks: ILibrarySocialMediaLinks;
  attributeFilters: ILibraryTenantAttributeFilters;
}

export interface ILibraryMeetingPlaceMinifiedLogo extends ILibraryMeetingPlaceMinified {
  logo: any;
  timezone: string;
}
export interface ILibraryMeetingPlaceMinifiedExtendedAttributeFilters
  extends ILibraryMeetingPlaceMinifiedLogo, ILibraryMeetingPlaceAddress {
  attributeFilters: any;
}
export interface ILibraryMeetingPlaceMinified {
  integrationId: string;
  businessUnitCode: string;
  showSocialShare: boolean;
}

export interface ILibraryMeetingPlace
  extends
    ILibraryMeetingPlaceMinified,
    ILibraryMeetingPlaceMinifiedLogo,
    ILibraryMeetingPlaceMinifiedOpeningHours,
    ILibraryMeetingPlaceMinifiedExtended,
    ILibraryMeetingPlaceSeoRichMinifiedExtended,
    ILibraryMeetingPlaceMinifiedCoordinates {
  sys: {
    id: string;
    locale: string;
  };
  contentful_id: string;
  whatsappNumber: number;
  phoneNumber: string;
  eMailAddress: string;
  primaryLanguage: ILibraryLanguage;
  languagesCollection?: {
    items: ILibraryLanguage[];
  };
  showLanguageSelector: boolean;
  integrationId: string;
  tenantListPage?: any;
  tenantShopListPage: IMeetingPlacePageTenantShopList;
  tenantFoodBeverageListPage?: any;
  tenantEntertainmentListPage?: any;
  serviceListPage?: IMeetingPlacePageServiceList;
  occationListPage?: {
    occasions: any;
  };
  eventListPage?: any;
  offerListPage?: IMeetingPlacePageOfferList;
  newsListPage?: any;
  leisurePackageOfferListPage?: any;
  openingHoursLink: IMeetingPlaceLinkToAnyPage;
}

export const LibraryMeetingPlaceAddressFragment = gql`
  fragment LibraryMeetingPlaceAddress on LibraryMeetingPlace {
    visitorsAddressStreet
    visitorsAddressCity
    visitorsAddressZipCode
    country {
      name
      countryCode
    }
  }
`;

export const LibraryMeetingPlaceMinifiedExtendedFragment = gql`
  fragment LibraryMeetingPlaceMinifiedExtended on LibraryMeetingPlace {
    ...LibraryMeetingPlaceMinifiedLogo
    node_locale
    name
    cardList {
      cards {
        ... on Node {
          ... on BlockTextAndImageCard {
            ...TextAndImageCardBlock
          }
        }
      }
    }
    mainBannerList {
      ... on LibraryMeetingPlaceMainBannerList {
        blocktype: __typename
        mainBannerItems {
          ...MainBannerBlock
        }
      }
    }
  }
`;

export const LibraryMeetingPlaceMinifiedFragment = gql`
  fragment LibraryMeetingPlaceMinified on LibraryMeetingPlace {
    integrationId
    businessUnitCode
    showSocialShare
  }
`;

export const LibraryMeetingPlaceMinifiedLogoFragment = gql`
  ${LibraryMeetingPlaceMinifiedFragment}
  ${SvgFragment}
  fragment LibraryMeetingPlaceMinifiedLogo on LibraryMeetingPlace {
    ...LibraryMeetingPlaceMinified
    logo {
      description
      ... on Asset {
        ...Svg
      }
    }
  }
`;

export const LibraryMeetingPlaceMinifiedCoordinatesFragment = gql`
  ${LibraryMeetingPlaceMinifiedFragment}
  fragment LibraryMeetingPlaceMinifiedCoordinates on LibraryMeetingPlace {
    ...LibraryMeetingPlaceMinified
    coordinates {
      lat
      lon
    }
  }
`;

export const LibraryMeetingPlaceSeoRichMinifiedExtendedFragment = gql`
  ${MainBannerBlockFragment}
  ${LibraryMeetingPlaceMinifiedLogoFragment}
  ${HomeTextAndImageCardBlockFragment}
  ${LibraryOpeningHoursFragment}
  ${LibraryMeetingPlaceMinifiedCoordinatesFragment}
  fragment LibraryMeetingPlaceSeoRichMinifiedExtended on LibraryMeetingPlace {
    ...LibraryMeetingPlaceMinifiedLogo
    name
    sys {
      id
    }
    phoneNumber
    eMailAddress
    webUrl
    cardList {
      cardsCollection {
        items {
          ... on BlockTextAndImageCard {
            ...HomeTextAndImageCardBlock
          }
        }
      }
      showIkeaProductsCard
    }
    mainBannerList {
      ... on LibraryMeetingPlaceMainBannerList {
        blocktype: __typename
        mainBannerItemsCollection {
          items {
            ...MainBannerBlock
          }
        }
      }
    }
    openingHours {
      ... on LibraryOpeningHours {
        ...LibraryOpeningHours
      }
    }
    ...LibraryMeetingPlaceMinifiedCoordinates
    visitorsAddressStreet
    visitorsAddressCity
    visitorsAddressZipCode
    country {
      name
      countryCode
    }
  }
`;

export const LibraryMeetingPlaceMinifiedOpeningHoursFragment = gql`
  ${LibraryTenantAttributeFiltersFragment}
  ${LibraryOpeningHoursFragment}
  ${LibraryOpeningHoursExceptionsFragment}
  ${LibrarySocialMediaLinksFragment}
  ${LibraryMeetingPlaceMinifiedFragment}
  fragment LibraryMeetingPlaceMinifiedOpeningHours on LibraryMeetingPlace {
    ...LibraryMeetingPlaceMinified
    visioGlobeMapKey
    visioGlobeFloorPlanPage {
      sys {
        id
      }
      slug
      name
    }
    openingHours {
      ... on LibraryOpeningHours {
        ...LibraryOpeningHours
      }
    }
    openingHoursExceptionsCollection {
      items {
        ...LibraryOpeningHoursExceptions
      }
    }
    timezone
    socialMediaLinks {
      ... on LibrarySocialMediaLinks {
        ...LibrarySocialMediaLinks
      }
    }
    attributeFilters {
      ... on LibraryTenantAttributeFilters {
        ...LibraryTenantAttributeFilters
      }
    }
  }
`;

export const LibraryMeetingPlaceMinifiedExtendedAttributeFiltersFragment = gql`
  ${LibraryTenantAttributeFiltersFragment}
  fragment LibraryMeetingPlaceMinifiedExtendedAttributeFilters on LibraryMeetingPlace {
    ...LibraryMeetingPlaceMinifiedLogo
    attributeFilters {
      ... on LibraryTenantAttributeFilters {
        ...LibraryTenantAttributeFilters
      }
    }
  }
`;

export const LibraryMeetingPlaceFragment = gql`
  fragment LibraryMeetingPlace on LibraryMeetingPlace {
    id
    phoneNumber
    eMailAddress
    showLanguageSelector
    primaryLanguage {
      ... on LibraryLanguage {
        ...LibraryLanguage
      }
    }
    languages {
      ... on LibraryLanguage {
        ...LibraryLanguage
      }
    }
    ...LibraryMeetingPlaceMinified
    ...LibraryMeetingPlaceMinifiedLogo
    ...LibraryMeetingPlaceMinifiedOpeningHours
    ...LibraryMeetingPlaceMinifiedExtended
    ...LibraryMeetingPlaceMinifiedCoordinates
  }
`;

